import { useState, useEffect } from 'react';
import LoadingSpinner from './LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import RescheduleModal from './RescheduleModal';
import SuccessRescheduleModal from './SuccessRescheduleModal';
import MiCuentaPaciente from './MiCuentaPaciente';

// Interface para el usuario
interface User {
  id: number;
  email: string;
  nombre: string;
  apellido: string;
  es_profesional: boolean;
  auth_method: string;
  picture?: string;
}

// Interface para citas reales del backend
interface Appointment {
  id: number;
  professional_id: number;
  patient_id: number;
  service_id: number;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  appointment_date: string;
  appointment_time: string;
  duration: number;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  price: number;
  deposit_required: boolean;
  deposit_amount?: number;
  deposit_paid: boolean;
  notes?: string;
  is_first_time: boolean;
  urgency_level?: 'low' | 'normal' | 'high';
  created_at: string;
  updated_at: string;
  // Datos adicionales de la vista
  service_name?: string;
  service_description?: string;
  service_category?: string;
  professional_name?: string;
  professional_especialidad?: string;
  professional_type?: string;
}

// Modal simple para ver detalles de cita
interface AppointmentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: Appointment | null;
}

const AppointmentDetailsModal: React.FC<AppointmentDetailsModalProps> = ({
  isOpen,
  onClose,
  appointment
}) => {
  if (!isOpen || !appointment) return null;

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'no_show': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Confirmada';
      case 'pending': return 'Pendiente';
      case 'completed': return 'Completada';
      case 'cancelled': return 'Cancelada';
      case 'no_show': return 'No asistió';
      default: return 'Desconocido';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Detalles de la Cita</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Contenido */}
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Información General</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-blue-700">Profesional:</span>
                <span className="text-blue-900 font-medium">{appointment.professional_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700">Especialidad:</span>
                <span className="text-blue-900">{appointment.professional_especialidad}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700">Servicio:</span>
                <span className="text-blue-900">{appointment.service_name}</span>
              </div>
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-2">Fecha y Hora</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-green-700">Fecha:</span>
                <span className="text-green-900 font-medium capitalize">{formatDate(appointment.appointment_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">Hora:</span>
                <span className="text-green-900 font-bold">{appointment.appointment_time}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">Duración:</span>
                <span className="text-green-900">{appointment.duration} minutos</span>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <h4 className="font-semibold text-purple-900 mb-2">Detalles del Servicio</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-purple-700">Precio:</span>
                <span className="text-purple-900 font-bold">${appointment.price}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-purple-700">Estado:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(appointment.status)}`}>
                  {getStatusText(appointment.status)}
                </span>
              </div>
              {appointment.deposit_required && (
                <div className="flex justify-between">
                  <span className="text-purple-700">Seña requerida:</span>
                  <span className="text-orange-600 font-medium">${appointment.deposit_amount}</span>
                </div>
              )}
            </div>
          </div>

          {appointment.notes && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Notas</h4>
              <p className="text-sm text-gray-700">{appointment.notes}</p>
            </div>
          )}
        </div>

        {/* Botón cerrar */}
        <div className="mt-6">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal de Emergencia
const EmergencyModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const emergencyContacts = [
    { name: 'SAME (Emergencias Médicas)', number: '107', color: 'bg-red-500', icon: '🚑' },
    { name: 'Bomberos', number: '100', color: 'bg-orange-500', icon: '🚒' },
    { name: 'Policía', number: '101', color: 'bg-blue-500', icon: '👮' },
    { name: 'Defensa Civil', number: '103', color: 'bg-green-500', icon: '🛡️' },
    { name: 'Violencia de Género', number: '144', color: 'bg-purple-500', icon: '👩' },
    // Antes figuraba el 135, que es de CABA y Gran Buenos Aires: desde
    // Corrientes o Chaco —donde están los pacientes— no conecta. Esta es la
    // línea del Ministerio de Salud y atiende de todo el país.
    { name: 'Salud mental — línea de ayuda', number: '0800-999-0091', color: 'bg-indigo-500', icon: '💙' }
  ];

  const handleCall = (number: string) => {
    if (window.confirm(`¿Llamar al ${number}?`)) {
      // Se marca solo con dígitos. En pantalla el número va con guiones
      // porque se lee mejor (0800-999-0091), pero no todos los teléfonos
      // limpian los separadores al recibir un tel:, y acá no es momento de
      // que a alguien le falle la llamada.
      window.open(`tel:${number.replace(/[^0-9+]/g, '')}`);
    }
  };

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4 animate-fadeIn"
        onClick={onClose}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto transform transition-all duration-300 scale-100 animate-slideIn"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-t-2xl p-6 text-white">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center animate-pulse">
                <span className="text-2xl">🚨</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold">EMERGENCIA MÉDICA</h2>
                <p className="text-red-100">Asistencia inmediata 24/7</p>
              </div>
            </div>
          </div>

          <div className="p-6 max-h-96 overflow-y-auto">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 animate-pulse">
              <div className="flex items-start space-x-2">
                <span className="text-yellow-500 text-lg">⚠️</span>
                <div>
                  <p className="text-yellow-800 font-semibold text-sm">IMPORTANTE</p>
                  <p className="text-yellow-700 text-xs">
                    Este es un servicio informativo. En caso de emergencia real, llama directamente a los números.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {emergencyContacts.map((contact) => (
                <div
                  key={contact.number}
                  className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-all duration-200 cursor-pointer transform hover:scale-105 emergency-card"
                  onClick={() => handleCall(contact.number)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 ${contact.color} rounded-full flex items-center justify-center text-white text-lg`}>
                        {contact.icon}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{contact.name}</p>
                        <p className="text-gray-600 text-xs">Disponible 24 horas</p>
                      </div>
                    </div>
                    {/* Se quitó el puntito verde con "Activo". Latía como si
                        estuviéramos comprobando que la línea responde, y no
                        comprobamos nada. En una pantalla de emergencias, dar
                        por buena una línea que no verificaste es de lo peor
                        que se puede hacer. */}
                    <div className="text-right">
                      <p className="font-bold text-lg text-gray-900">{contact.number}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 text-sm mb-2">📋 En caso de emergencia:</h3>
              <ul className="text-xs text-gray-700 space-y-1">
                <li>• Mantén la calma y respira profundamente</li>
                <li>• Proporciona tu ubicación exacta</li>
                <li>• Describe claramente la situación</li>
                <li>• Sigue las instrucciones del operador</li>
                <li>• No cuelgues hasta que te lo indiquen</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-2xl">
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors duration-200 transform hover:scale-105"
              >
                Cerrar
              </button>
              <button
                onClick={() => handleCall('107')}
                className="flex-1 bg-red-500 text-white py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors duration-200 transform hover:scale-105 flex items-center justify-center space-x-2"
              >
                <span>🚑 Llamar al 107</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: scale(0.7) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }
        
        .emergency-card:hover {
          transform: translateY(-2px) scale(1.05);
          box-shadow: 0 10px 25px rgba(239, 68, 68, 0.15);
        }
      `}</style>
    </>
  );
};

const PatientDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  // La cita pasada más reciente a la que sí fue. `null` = todavía ninguna.
  const [ultimaConsulta, setUltimaConsulta] = useState<Appointment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [isEmergencyModalOpen, setIsEmergencyModalOpen] = useState(false);

  // Estados para reagendamiento
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [selectedAppointmentForReschedule, setSelectedAppointmentForReschedule] = useState<Appointment | null>(null);

  // Estados para modal de detalles
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedAppointmentForDetails, setSelectedAppointmentForDetails] = useState<Appointment | null>(null);

  // Estados para modal de éxito
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [rescheduledAppointmentInfo, setRescheduledAppointmentInfo] = useState<{
    oldDate: string;
    oldTime: string;
    newDate: string;
    newTime: string;
    professionalName: string;
    serviceName: string;
  } | null>(null);

  useEffect(() => {
    // Verificar si el usuario está logueado
    const userData = localStorage.getItem('user');
    const loginStatus = localStorage.getItem('isLoggedIn');
    
    if (!userData || loginStatus !== 'true') {
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      
      // Verificar que sea un paciente
      if (parsedUser.es_profesional) {
        navigate('/dashboard/profesional');
        return;
      }
      
      setUser(parsedUser);
      
      // Cargar citas del paciente
      loadPatientAppointments(parsedUser.id);
      
    } catch (error) {
      console.error('Error parsing user data:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Función para cargar las citas del paciente desde el backend
  const loadPatientAppointments = async (patientId: number) => {
    setAppointmentsLoading(true);
    setError(null);
    
    try {
      // El endpoint pasa a pedir token y a verificar que los datos sean tuyos.
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/patients/${patientId}/appointments`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: Error al cargar citas`);
      }

      const data = await response.json();
      
      if (data.success && Array.isArray(data.appointments)) {
        // La última consulta real. Antes el panel decía "Hace 2 meses" escrito
        // a mano, igual para todo el mundo. Se calcula con las citas que ya
        // pasaron y a las que efectivamente fue: una cancelada o un ausente
        // no son una consulta.
        const ahora = new Date();
        const pasadas = data.appointments
          .filter((apt: Appointment) => {
            const cuando = new Date(`${apt.appointment_date} ${apt.appointment_time}`);
            return cuando < ahora && apt.status !== 'cancelled' && apt.status !== 'no_show';
          })
          .sort((a: Appointment, b: Appointment) =>
            new Date(`${b.appointment_date} ${b.appointment_time}`).getTime() -
            new Date(`${a.appointment_date} ${a.appointment_time}`).getTime()
          );
        setUltimaConsulta(pasadas[0] || null);

        // Filtrar solo citas futuras y ordenar por fecha
        const futureAppointments = data.appointments
          .filter((apt: Appointment) => {
            const appointmentDate = new Date(`${apt.appointment_date} ${apt.appointment_time}`);
            return appointmentDate >= new Date() || apt.status === 'pending' || apt.status === 'confirmed';
          })
          .sort((a: Appointment, b: Appointment) => {
            const dateA = new Date(`${a.appointment_date} ${a.appointment_time}`);
            const dateB = new Date(`${b.appointment_date} ${b.appointment_time}`);
            return dateA.getTime() - dateB.getTime();
          });
        
        setAppointments(futureAppointments);
      } else {
        setAppointments([]);
      }
    } catch (err: any) {
      console.error('Error cargando citas del paciente:', err);
      setError(`Error al cargar citas: ${err.message}`);
      setAppointments([]);
    } finally {
      setAppointmentsLoading(false);
    }
  };

  // Función para cancelar una cita
  const handleCancelAppointment = async (appointmentId: number) => {
    if (!user?.id) return;
    
    const confirmCancel = window.confirm('¿Estás seguro de que quieres cancelar esta cita?');
    if (!confirmCancel) return;
    
    setActionLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/patients/${user.id}/appointments/${appointmentId}/cancel`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          cancellationReason: 'Cancelada por el paciente desde el dashboard'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Dentro de las 48 hs no se cancela solo, pero no se lo deja sin
        // salida: se le ofrece escribirle al profesional. Si le cerramos la
        // puerta y nada más, no viene igual y nadie se entera, que es peor
        // que la cancelación.
        if (errorData.code === 'FUERA_DE_PLAZO') {
          const tel = errorData.profesional?.telefono;
          const nombre = errorData.profesional?.nombre || 'el profesional';
          if (tel && window.confirm(`${errorData.error}\n\n¿Querés escribirle ahora a ${nombre} por WhatsApp?`)) {
            const texto = encodeURIComponent(
              'Hola, no voy a poder asistir al turno que tengo reservado. ¿Lo podemos reprogramar?'
            );
            window.open(`https://wa.me/${String(tel).replace(/\D/g, '')}?text=${texto}`, '_blank');
          } else if (!tel) {
            alert(errorData.error);
          }
          return;
        }

        throw new Error(errorData.error || 'Error al cancelar la cita');
      }

      alert('Cita cancelada exitosamente. Le avisamos al profesional.');
      
      // Recargar las citas
      loadPatientAppointments(user.id);
      
    } catch (err: any) {
      console.error('Error cancelando cita:', err);
      alert(`Error al cancelar la cita: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Función para reagendar
  const handleRescheduleAppointment = (appointment: Appointment) => {
    setSelectedAppointmentForReschedule(appointment);
    setIsRescheduleModalOpen(true);
  };

  // Función para manejar éxito del reagendamiento
  const handleRescheduleSuccess = (newDate: string, newTime: string) => {
    if (selectedAppointmentForReschedule) {
      // Guardar la información del reagendamiento
      setRescheduledAppointmentInfo({
        oldDate: selectedAppointmentForReschedule.appointment_date,
        oldTime: selectedAppointmentForReschedule.appointment_time,
        newDate: newDate,
        newTime: newTime,
        professionalName: selectedAppointmentForReschedule.professional_name || 'Profesional',
        serviceName: selectedAppointmentForReschedule.service_name || 'Servicio'
      });
      
      // Mostrar modal de éxito
      setShowSuccessModal(true);
      
      // Recargar citas
      if (user?.id) {
        loadPatientAppointments(user.id);
      }
    }
    
    // Cerrar modal de reagendamiento
    setIsRescheduleModalOpen(false);
    setSelectedAppointmentForReschedule(null);
  };

  // Función para ver detalles
  const handleViewAppointment = (appointmentId: number) => {
    const appointment = appointments.find(apt => apt.id === appointmentId);
    if (appointment) {
      setSelectedAppointmentForDetails(appointment);
      setShowDetailsModal(true);
    }
  };

  // Funciones para acciones rápidas
  const handleSearchDoctors = () => {
    navigate('/search');
  };

  const handleBookAppointment = () => {
    navigate('/search');
  };

  const handleViewProfile = () => {
    navigate('/profile');
  };

  const handleEmergency = () => {
    setIsEmergencyModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size={56} label="Cargando dashboard..." />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const getInitials = (nombre: string, apellido: string) => {
    return `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'no_show': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Confirmada';
      case 'pending': return 'Pendiente';
      case 'completed': return 'Completada';
      case 'cancelled': return 'Cancelada';
      case 'no_show': return 'No asistió';
      default: return 'Desconocido';
    }
  };

  const canCancelAppointment = (appointment: Appointment) => {
    const dateOnly = appointment.appointment_date.split('T')[0];
    const timeOnly = appointment.appointment_time.substring(0, 5);
    const appointmentDateTime = new Date(`${dateOnly} ${timeOnly}`);
    const now = new Date();
    const hoursDifference = (appointmentDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);

    // Permitir cancelar si faltan más de 2 horas y el estado es pending o confirmed
    return hoursDifference > 2 && (appointment.status === 'pending' || appointment.status === 'confirmed');
  };

  const canRescheduleAppointment = (appointment: Appointment) => {
    // Extraer solo la fecha sin la hora del backend
    const dateOnly = appointment.appointment_date.split('T')[0];
    const timeOnly = appointment.appointment_time.substring(0, 5);
    
    // Crear la fecha correctamente
    const appointmentDateTime = new Date(`${dateOnly} ${timeOnly}`);
    const now = new Date();
    
    return appointmentDateTime > now && (appointment.status === 'pending' || appointment.status === 'confirmed');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Modal de Emergencia */}
      <EmergencyModal 
        isOpen={isEmergencyModalOpen} 
        onClose={() => setIsEmergencyModalOpen(false)} 
      />

      {/* Modal de Reagendamiento */}
<RescheduleModal
  isOpen={isRescheduleModalOpen}
  onClose={() => {
    setIsRescheduleModalOpen(false);
    setSelectedAppointmentForReschedule(null);
  }}
  appointment={selectedAppointmentForReschedule}
  onSuccess={handleRescheduleSuccess}
/>

{/* Modal de Éxito de Reagendamiento */}
{rescheduledAppointmentInfo && (
  <SuccessRescheduleModal
    isOpen={showSuccessModal}
    onClose={() => {
      setShowSuccessModal(false);
      setRescheduledAppointmentInfo(null);
    }}
    oldDate={rescheduledAppointmentInfo.oldDate}
    oldTime={rescheduledAppointmentInfo.oldTime}
    newDate={rescheduledAppointmentInfo.newDate}
    newTime={rescheduledAppointmentInfo.newTime}
    professionalName={rescheduledAppointmentInfo.professionalName}
    serviceName={rescheduledAppointmentInfo.serviceName}
  />
)}

      {/* Modal de Detalles de Cita */}
      <AppointmentDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedAppointmentForDetails(null);
        }}
        appointment={selectedAppointmentForDetails}
      />

      {/* Header del Dashboard */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={`${user.nombre} ${user.apellido}`}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center text-white text-xl font-semibold">
                  {getInitials(user.nombre, user.apellido)}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {getGreeting()}, {user.nombre}!
                </h1>
                <p className="text-gray-600">
                  Aquí tienes un resumen de tu actividad médica
                </p>
              </div>
            </div>
            
            {/* Botón de emergencia */}
            <button 
              onClick={handleEmergency}
              className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <div className="relative">
                <svg className="w-6 h-6 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L5.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <span className="font-bold">EMERGENCIA</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Mostrar error si existe */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <span className="text-red-500 mr-2">⌫</span>
              <span className="text-red-700">{error}</span>
              <button 
                onClick={() => user?.id && loadPatientAppointments(user.id)}
                className="ml-auto text-red-600 hover:text-red-800 font-medium"
              >
                Reintentar
              </button>
            </div>
          </div>
        )}

        {/* Acciones rápidas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <button
            onClick={handleBookAppointment}
            className="flex items-center justify-center space-x-2 bg-blue-600 text-white p-4 rounded-xl hover:bg-blue-700 transition-colors transform hover:scale-105"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span className="font-medium">Agendar Turno</span>
          </button>
          
          <button
            onClick={handleViewProfile}
            className="flex items-center justify-center space-x-2 bg-green-600 text-white p-4 rounded-xl hover:bg-green-700 transition-colors transform hover:scale-105"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="font-medium">Mi Perfil</span>
          </button>
          
          <button
            onClick={() => navigate('/medical-history')}
            className="flex items-center justify-center space-x-2 bg-purple-600 text-white p-4 rounded-xl hover:bg-purple-700 transition-colors transform hover:scale-105"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span className="font-medium">Mi Historial Médico</span>
          </button>
          
          <button
            onClick={handleSearchDoctors}
            className="flex items-center justify-center space-x-2 bg-orange-600 text-white p-4 rounded-xl hover:bg-orange-700 transition-colors transform hover:scale-105"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="font-medium">Buscar Doctores</span>
          </button>
        </div>

        {/* Grid principal */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Próximos Turnos */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">Próximas Citas</h2>
                  <div className="flex items-center space-x-3">
                    {appointmentsLoading && (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                    )}
                    <button
                      onClick={handleBookAppointment}
                      className="text-blue-500 hover:text-blue-600 font-medium text-sm flex items-center space-x-1"
                    >
                      <span>Agendar nueva</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                {appointmentsLoading ? (
                  <div className="space-y-4">
                    {[1, 2].map(i => (
                      <div key={i} className="animate-pulse">
                        <div className="h-24 bg-gray-200 rounded-lg"></div>
                      </div>
                    ))}
                  </div>
                ) : appointments.length > 0 ? (
                  <div className="space-y-4">
                    {appointments.map((appointment) => (
                      <div 
                        key={appointment.id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center">
                              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                {appointment.professional_name || 'Profesional'}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {appointment.professional_especialidad || appointment.service_category || 'Especialidad'}
                              </p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                            {getStatusText(appointment.status)}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>
                              {(() => {
                                const p = appointment.appointment_date.split('T')[0].split('-');
                                return new Date(parseInt(p[0]), parseInt(p[1]) - 1, parseInt(p[2]), 12).toLocaleDateString('es-ES', {
                                  weekday: 'short',
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                });
                              })()}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{appointment.appointment_time}</span>
                            <span className="text-gray-400">•</span>
                            <span>{appointment.duration} min</span>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          {/* Ver Detalles */}
                          <button
                            onClick={() => handleViewAppointment(appointment.id)}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors flex items-center space-x-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <span>Ver Detalles</span>
                          </button>
                          
                          {/* Reagendar */}
                          {canRescheduleAppointment(appointment) && (
                            <button
                              onClick={() => handleRescheduleAppointment(appointment)}
                              disabled={actionLoading}
                              className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 transition-colors flex items-center space-x-1 disabled:opacity-50"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span>Reagendar</span>
                            </button>
                          )}
                          
                          {/* Cancelar */}
                          {canCancelAppointment(appointment) && (
                            <button
                              onClick={() => handleCancelAppointment(appointment.id)}
                              disabled={actionLoading}
                              className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200 transition-colors flex items-center space-x-1 disabled:opacity-50"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              <span>Cancelar</span>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No tienes citas programadas</h3>
                    <p className="text-gray-600 mb-4">Agenda tu primera cita médica</p>
                    <button
                      onClick={handleBookAppointment}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Agendar Turno
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Tus turnos, con fechas de verdad.
                Antes esta tarjeta decía "Última consulta: Hace 2 meses" y
                "Alergias: Ninguna" escritos a mano, iguales para todos. Lo de
                las alergias era lo más grave: una afirmación médica inventada
                sobre la persona que la está leyendo. Si alguien alérgico a la
                penicilina abría su panel, le decíamos que no tenía alergias.
                Se fue del todo: no tenemos ese dato en ningún lado. */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tus turnos</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-600">Última consulta</span>
                  <span className="text-gray-900 font-medium text-right">
                    {ultimaConsulta
                      ? (() => {
                          const p = ultimaConsulta.appointment_date.split('T')[0].split('-');
                          return new Date(parseInt(p[0]), parseInt(p[1]) - 1, parseInt(p[2]), 12)
                            .toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
                        })()
                      : 'Todavía ninguna'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-600">Próximo turno</span>
                  <span className="text-gray-900 font-medium text-right">
                    {appointments.length > 0
                      ? (() => {
                          const p = appointments[0].appointment_date.split('T')[0].split('-');
                          return new Date(parseInt(p[0]), parseInt(p[1]) - 1, parseInt(p[2]), 12)
                            .toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
                        })()
                      : 'No programado'}
                  </span>
                </div>
              </div>
            </div>

            {/* Sus profesionales y sus recetas. El componente decide solo qué
                mostrar: si no hay datos, no dibuja nada. */}
            {user?.id && <MiCuentaPaciente patientId={user.id} />}

            {/* La tarjeta "Recordatorios" se eliminó entera.
                Mostraba "Control anual" y "Vacunación" fijos para todos, sin
                salir de ningún dato. A un paciente eso le parece un aviso
                sobre SU salud, y no era nada: era decoración con forma de
                indicación médica. */}

            {/* Enlaces rápidos: solo los que llevan a algún lado.
                Antes había tres botones sin `onClick` — se hacía clic en "Ver
                recetas médicas" y no pasaba nada. Ese quedó afuera hasta que
                exista la pantalla; prometer algo que no está es peor que no
                ofrecerlo. */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Enlaces rápidos</h3>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/medical-history')}
                  className="w-full text-left text-blue-600 hover:text-blue-700 text-sm py-2"
                >
                  Ver mi historial de turnos
                </button>
                <button
                  onClick={() => navigate('/profile')}
                  className="w-full text-left text-blue-600 hover:text-blue-700 text-sm py-2"
                >
                  Mis datos
                </button>
                <a
                  href="https://wa.me/5493794080538"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-left text-blue-600 hover:text-blue-700 text-sm py-2"
                >
                  Contactar soporte
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDashboard;